import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, TrendingUp, Save, RefreshCw } from 'lucide-react';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api' 
  : '/api';

function CommissionSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commissions, setCommissions] = useState({
    STANDART: 0.05,
    VIP: 0.02,
    KOMISYONSUZ: 0.00
  });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/commission-settings`, axiosConfig);
      setCommissions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/commission-settings`, commissions, axiosConfig);
      alert('✅ Komisyon oranları başarıyla güncellendi!');
      setSaving(false);
    } catch (error) {
      console.error('Failed to save commissions:', error);
      alert('❌ Komisyon güncelleme hatası!');
      setSaving(false);
    }
  };

  const handleChange = (type, value) => {
    setCommissions(prev => ({
      ...prev,
      [type]: parseFloat(value)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Komisyon Ayarları</h1>
            <p className="text-gray-400">Hesap tiplerine göre komisyon oranlarını ayarlayın</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            ← Geri Dön
          </button>
        </div>

        {/* Commission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* STANDART */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-blue-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">STANDART</h3>
                <p className="text-gray-400 text-sm">Normal hesaplar</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Komisyon Oranı (%)</label>
              <input
                type="number"
                step="0.01"
                value={commissions.STANDART}
                onChange={(e) => handleChange('STANDART', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xl font-bold"
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Örnek İşlem:</div>
              <div className="text-white text-sm">₺10,000 × %{commissions.STANDART}</div>
              <div className="text-blue-400 font-bold text-lg">= ₺{(10000 * commissions.STANDART / 100).toFixed(2)}</div>
            </div>
          </div>

          {/* VIP */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-yellow-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-500/10 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">⭐ VIP</h3>
                <p className="text-gray-400 text-sm">İndirimli komisyon</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Komisyon Oranı (%)</label>
              <input
                type="number"
                step="0.01"
                value={commissions.VIP}
                onChange={(e) => handleChange('VIP', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-xl font-bold"
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Örnek İşlem:</div>
              <div className="text-white text-sm">₺10,000 × %{commissions.VIP}</div>
              <div className="text-yellow-400 font-bold text-lg">= ₺{(10000 * commissions.VIP / 100).toFixed(2)}</div>
            </div>
          </div>

          {/* KOMISYONSUZ */}
          <div className="bg-gray-800 rounded-xl p-6 border-2 border-green-500 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-500/10 p-3 rounded-lg">
                <RefreshCw className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">💎 KOMİSYONSUZ</h3>
                <p className="text-gray-400 text-sm">Komisyon yok</p>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-2">Komisyon Oranı (%)</label>
              <input
                type="number"
                step="0.01"
                value={commissions.KOMISYONSUZ}
                onChange={(e) => handleChange('KOMISYONSUZ', e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-xl font-bold"
                disabled
              />
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Örnek İşlem:</div>
              <div className="text-white text-sm">₺10,000 × %{commissions.KOMISYONSUZ}</div>
              <div className="text-green-400 font-bold text-lg">= ₺0.00</div>
            </div>
          </div>
        </div>

        {/* Asset Category Examples */}
        <div className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700">
          <h2 className="text-white font-bold text-xl mb-4">Varlık Kategorilerine Göre Örnekler</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* BIST 100 */}
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
                <span>📈</span>
                BIST 100 Hisseleri
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">ASELS (₺188.30 × 10 adet)</span>
                  <span className="text-white font-bold">₺1,883</span>
                </div>
                <div className="border-t border-gray-600 pt-2">
                  <div className="flex justify-between text-gray-400">
                    <span>STANDART (%{commissions.STANDART}):</span>
                    <span className="text-blue-400">₺{(1883 * commissions.STANDART / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>VIP (%{commissions.VIP}):</span>
                    <span className="text-yellow-400">₺{(1883 * commissions.VIP / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>KOMİSYONSUZ:</span>
                    <span className="text-green-400">₺0.00</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Kripto */}
            <div className="bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
                <span>₿</span>
                Kripto Paralar
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">BTC ($91,602 × 0.1 adet)</span>
                  <span className="text-white font-bold">$9,160</span>
                </div>
                <div className="border-t border-gray-600 pt-2">
                  <div className="flex justify-between text-gray-400">
                    <span>STANDART (%{commissions.STANDART}):</span>
                    <span className="text-blue-400">$${(9160 * commissions.STANDART / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>VIP (%{commissions.VIP}):</span>
                    <span className="text-yellow-400">${(9160 * commissions.VIP / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>KOMİSYONSUZ:</span>
                    <span className="text-green-400">$0.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={() => fetchCommissions()}
            className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-4 rounded-lg transition-colors font-semibold flex items-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Sıfırla
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg transition-all font-semibold flex items-center gap-2 shadow-lg disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>

        {/* Info */}
        <div className="mt-8 bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <div className="text-blue-400 text-sm">
            <strong>💡 Bilgi:</strong> Komisyon oranları tüm işlemlerde otomatik olarak uygulanır. 
            Değişiklikler hemen yürürlüğe girer.
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommissionSettings;
